import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const FACTORS = [
  { letter: "D", label: "Dominância", color: "#E5453B" },
  { letter: "I", label: "Influência", color: "#F5A623" },
  { letter: "S", label: "Estabilidade", color: "#00DCA4" },
  { letter: "C", label: "Conformidade", color: "#4A90D9" },
];

function ConfirmModal({ onConfirm, onCancel }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 lg:left-64 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0, 51, 38, 0.6)" }}
      onClick={onCancel}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ duration: 0.2 }}
        className="bg-white rounded-2xl w-full max-w-md overflow-hidden"
        style={{ boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-5">
            <h2
              className="text-lg font-bold"
              style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}
            >
              Antes de começar
            </h2>
            <button
              onClick={onCancel}
              className="p-1 rounded-lg hover:bg-gray-100 transition-colors -mr-1 -mt-1"
            >
              <X className="w-4 h-4" style={{ color: "#99A6AB" }} />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-4 mb-6">
            <div
              className="rounded-xl p-4"
              style={{ background: "#EAFAF6" }}
            >
              <p
                className="text-sm font-semibold mb-1"
                style={{ fontFamily: "Work Sans, sans-serif", color: "#00664C" }}
              >
                Reserve pelo menos 10 minutos
              </p>
              <p
                className="text-sm leading-relaxed"
                style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}
              >
                Encontre um lugar tranquilo, sem interrupções. Respostas feitas com
                pressa ou distração comprometem a precisão do seu perfil.
              </p>
            </div>

            <div
              className="rounded-xl p-4"
              style={{ background: "#FFF8EC", border: "1px solid #F5E6C8" }}
            >
              <p
                className="text-sm font-semibold mb-1"
                style={{ fontFamily: "Work Sans, sans-serif", color: "#8B6914" }}
              >
                Você só poderá refazer em 90 dias
              </p>
              <p
                className="text-sm leading-relaxed"
                style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}
              >
                O DISC mede padrões estáveis de comportamento. Refazer com
                frequência não muda seu perfil real — só distorce o resultado.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <button
              onClick={() => { onCancel(); onConfirm(); }}
              className="w-full py-3.5 rounded-full text-sm font-semibold transition-all duration-200 hover:shadow-md"
              style={{
                fontFamily: "Work Sans, sans-serif",
                background: "#00DCA4",
                color: "#003326",
              }}
            >
              Estou pronto, iniciar
            </button>
            <button
              onClick={onCancel}
              className="w-full py-3 rounded-full text-sm font-medium transition-colors hover:bg-gray-50"
              style={{
                fontFamily: "Work Sans, sans-serif",
                color: "#99A6AB",
              }}
            >
              Voltar
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function DiscIntro({ onStart }) {
  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
        className="flex flex-col items-center justify-center py-12 px-4"
      >
        <div className="max-w-lg w-full text-center">
          <p
            className="text-xs font-medium uppercase tracking-widest mb-4"
            style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}
          >
            Análise Comportamental
          </p>

          <h1
            className="text-3xl font-bold mb-3"
            style={{ fontFamily: "Poppins, sans-serif", lineHeight: 1.25, color: "#003326" }}
          >
            Descubra seu perfil DISC
          </h1>

          <p
            className="text-sm leading-relaxed mb-8"
            style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}
          >
            Cada pessoa tem um jeito natural de agir, se comunicar e resolver problemas.
            Aqui você descobre o seu — e como usar isso a seu favor na carreira.
          </p>

          {/* DISC factor pills */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {FACTORS.map((f) => (
              <div key={f.letter} className="flex items-center gap-1.5">
                <span
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white"
                  style={{ background: f.color }}
                >
                  {f.letter}
                </span>
                <span
                  className="text-xs font-medium hidden sm:inline"
                  style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}
                >
                  {f.label}
                </span>
              </div>
            ))}
          </div>

          <button
            onClick={() => setShowConfirm(true)}
            className="w-full py-3.5 rounded-full text-sm font-semibold transition-all duration-200 hover:shadow-md"
            style={{
              fontFamily: "Work Sans, sans-serif",
              background: "#00DCA4",
              color: "#003326",
            }}
          >
            Iniciar análise
          </button>

          <p
            className="text-[11px] mt-5"
            style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}
          >
            Não há respostas certas ou erradas. Responda com honestidade.
          </p>
        </div>
      </motion.div>

      <AnimatePresence>
        {showConfirm && (
          <ConfirmModal
            onConfirm={onStart}
            onCancel={() => setShowConfirm(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
